import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { LegalLink } from '../../app/AppShell';
import { useLocale, useOptionalWs } from '../../app/hooks';
import type { BillingNotices as Notices } from '../../api/types';
import { Alert } from '../../components/ui';
import { cn } from '../../lib/cn';
import { errorMessage } from '../../i18n/labels';
import { localized } from './plans';
import { planLimitInfo } from './plan-limit';

/**
 * FR-BIL-13: "Mock payment mode: no real charges" on every billing screen while the backend runs
 * in mock mode. The text is the API's, in the UI language.
 */
export function MockModeBanner({ notices }: { notices: Notices | null | undefined }) {
  const locale = useLocale();
  if (!notices?.mockMode) return null;
  return (
    <Alert tone="warning" role="status" className="font-medium">
      <span data-testid="mock-banner">{localized(notices.mockMode, locale)}</span>
    </Alert>
  );
}

/**
 * FR-BIL-12: "Payment services provided by TapPay (Cherri Tech)" wherever a user pays or manages
 * payment (pricing, purchase and upgrade dialogs, billing page). Text from the API.
 */
export function PaymentProviderNotice({
  notices,
  className,
}: {
  notices: Notices | null | undefined;
  className?: string;
}) {
  const locale = useLocale();
  if (!notices) return null;
  return (
    <p
      className={cn(
        'flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400',
        className,
      )}
      data-testid="payment-provider-notice"
    >
      <svg viewBox="0 0 24 24" className="size-3.5 shrink-0" fill="none" aria-hidden="true">
        <path
          d="M7 11V8a5 5 0 0 1 10 0v3M6 11h12v9H6z"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinejoin="round"
        />
      </svg>
      {localized(notices.paymentProvider, locale)}
    </p>
  );
}

/**
 * §5.13 terms in short next to the plans: monthly, renews automatically, starts at once, no
 * refunds, cancel any time. Shown beside the offer, not only in the terms.
 */
export function RefundNote({ className }: { className?: string }) {
  return (
    <p
      className={cn('text-xs text-slate-500 dark:text-slate-400', className)}
      data-testid="refund-note"
    >
      <Trans
        i18nKey="pricing.refundNote"
        components={{ refund: <LegalLink page="refund" className="link" /> }}
      />
    </p>
  );
}

/**
 * An API error as an alert. Plan limits (FR-BIL-01) get the numbers and a way forward: the plans
 * page, which says whose plan covers the workspace (FR-BIL-16).
 */
export function ErrorAlert({ error, className }: { error: unknown; className?: string }) {
  const { t } = useTranslation();
  const ws = useOptionalWs();
  if (!error) return null;
  const info = planLimitInfo(error);
  if (!info) {
    return (
      <Alert tone="danger" className={className}>
        {errorMessage(t, error)}
      </Alert>
    );
  }
  return (
    <Alert tone="warning" role="alert" title={t('planLimit.title')} className={className}>
      <p data-testid="plan-limit-message">{errorMessage(t, error)}</p>
      <PlansLink ws={ws?.id} />
    </Alert>
  );
}

/**
 * The way forward from a plan limit: the workspace's plans page (it names the billing owner and,
 * for them, links to their account's plans), or the account's own plans outside a workspace.
 */
function PlansLink({ ws }: { ws: string | undefined }) {
  const { t } = useTranslation();
  return (
    <Link
      to={ws ? `/w/${ws}/billing/plans` : '/account/billing/plans'}
      className="link mt-1 inline-block font-medium"
    >
      {t('planLimit.seePlans')}
    </Link>
  );
}

/**
 * A feature the workspace's plan does not include (from `GET …/plan`), shown before anyone tries
 * it: the message, and the plans page.
 */
export function PlanFeatureNotice({ message, className }: { message: string; className?: string }) {
  const ws = useOptionalWs();
  return (
    <Alert tone="info" role="status" className={className}>
      <p data-testid="plan-feature-notice">{message}</p>
      <PlansLink ws={ws?.id} />
    </Alert>
  );
}
